import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Linkedin, Lock, Medal } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
export const badgeSrc = (url) => (url?.startsWith("/api") ? `${API}${url}` : url);

export const CourseBadgeCard = ({ badge, compact = false }) => {
  const { t } = useLang();
  const share = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/verify/${badge.serial || ""}`)}`;
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="card-dark p-4 text-center" data-testid={`course-badge-${badge.course_id}`}>
      <img src={badgeSrc(badge.image_url)} alt={badge.title} className={`${compact ? "w-24 h-24" : "w-40 h-40"} mx-auto object-contain`} />
      <p className="font-bold text-sm text-[#0B1B3A] mt-2 leading-tight">{badge.title}</p>
      {badge.earned_at && <p className="text-[11px] text-[#64748B] mt-1">{new Date(badge.earned_at).toLocaleDateString("el-GR")}</p>}
      {!compact && (
        <div className="flex justify-center gap-2 mt-3">
          <a href={badgeSrc(badge.image_url)} download={`badge-${badge.course_id}.png`} className="btn-outline-light !px-3 !py-1.5 text-xs" data-testid={`course-badge-download-${badge.course_id}`}><Download className="w-3.5 h-3.5" /> PNG</a>
          <a href={share} target="_blank" rel="noreferrer" className="btn-blue !px-3 !py-1.5 text-xs" data-testid={`course-badge-linkedin-${badge.course_id}`}><Linkedin className="w-3.5 h-3.5" /> {t("badges.share")}</a>
        </div>
      )}
    </motion.div>
  );
};

export const CourseBadges = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/badges/mine").then((r) => setData(r.data)).catch(() => setData({ badges: [], tiers: [] })); }, []);
  if (!data) return null;
  return (
    <section className="mt-14" data-testid="course-badges-section">
      <div className="flex items-center gap-3">
        <Medal className="w-6 h-6 text-[#1D4ED8]" />
        <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{t("badges.title")}</h2>
      </div>
      <p className="text-sm text-[#64748B] mt-2">{t("badges.subtitle")}</p>

      <div className="grid grid-cols-3 gap-3 mt-6 max-w-xl" data-testid="tier-badges">
        {data.tiers.map((tier) => (
          <div key={tier.key} className={`card-dark p-3 text-center relative ${tier.unlocked ? "" : "opacity-50 grayscale"}`} data-testid={`tier-badge-${tier.key}`}>
            <img src={badgeSrc(tier.image_url)} alt={tier.label} className="w-20 h-20 mx-auto object-contain" />
            <p className="font-bold text-xs text-[#0B1B3A] mt-1">{tier.label}</p>
            <p className="text-[10px] text-[#64748B]">{tier.needed}+ {t("badges.courses")}</p>
            {!tier.unlocked && <Lock className="w-4 h-4 text-[#64748B] absolute top-2 right-2" />}
          </div>
        ))}
      </div>
      {data.next_tier && <p className="text-xs font-bold text-[#1D4ED8] mt-3" data-testid="tier-next">{t("badges.next")} {data.next_tier.label}: {data.next_tier.needed} {t("badges.moreCourses")}</p>}

      {data.badges.length === 0 ? (
        <div className="card-dark p-10 mt-6 text-center text-[#64748B]" data-testid="course-badges-empty">{t("badges.empty")}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6" data-testid="course-badges-grid">
          {data.badges.map((b) => <CourseBadgeCard key={b.course_id} badge={b} />)}
        </div>
      )}
    </section>
  );
};

export default CourseBadges;
